import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MuiSwitch, { type SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import type { SxProps } from '@mui/material/styles';

import type { JSX } from 'react';
import { DesignSystemScope } from '../theme/DesignSystemScope.tsx';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled.ts';
import { SWITCH_FIELD_DESIGN_SYSTEM_KEYS } from './switchFieldDesignSystem.ts';

export type SwitchFieldProps = Partial<MuiSwitchProps> & {
  'data-test'?: string;
  label?: string | JSX.Element;
  message?: string;
  error?: boolean;
  margin?: 'dense' | 'normal' | 'none';
  formControlLabelSx?: SxProps;
  /** Forces the design-system (`true`) or legacy (`false`) version; defaults to the `ui-design-system` GrowthBook feature. */
  designSystem?: boolean;
};

export function SwitchField(props: SwitchFieldProps): JSX.Element {
  const {
    label,
    error,
    message,
    'data-test': dataTest,
    margin = 'none',
    formControlLabelSx,
    designSystem,
    ...withoutCustomProps
  } = props;
  const isDesignSystem = useDesignSystemEnabled('switchField', designSystem);

  const defaultProps = {
    color: 'primary',
    disableRipple: true,
  } as Partial<SwitchFieldProps>;

  const switchField = (
    <FormControl error={error} data-test={dataTest} margin={margin}>
      <FormControlLabel
        sx={formControlLabelSx}
        control={<MuiSwitch {...defaultProps} {...withoutCustomProps} data-test={`${dataTest}-switch`} />}
        label={label || ''}
      />
      {message && <FormHelperText>{message}</FormHelperText>}
    </FormControl>
  );

  return isDesignSystem ? (
    <DesignSystemScope keys={SWITCH_FIELD_DESIGN_SYSTEM_KEYS}>{switchField}</DesignSystemScope>
  ) : (
    switchField
  );
}
